import React from "react";
import { Text, View, Image, StyleSheet } from "@react-pdf/renderer";
import dayjs from "dayjs";
import { getAnalysisLayout } from "./analysisLayouts";
import { getFieldValue } from "./fieldMappings";

/**
 * Helper to safely extract analysis code from dataObjects
 * Handles both direct objects and {label, value} format
 */
const getAnalysisCode = (dataObjects: any): string => {
  const typeAnalysis = dataObjects?.typeAnalysis;
  if (!typeAnalysis) return '';
  // Handle {label, value} format
  if (typeAnalysis.value) return typeAnalysis.value;
  // Handle direct object
  return typeAnalysis?.codigo || '';
};

export const styles = StyleSheet.create({
  page: {
    fontSize: 12,
    flexDirection: "column",
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  logo: {
    width: 120,
  },
  labInfo: {
    display: "flex",
    flexDirection: "column",
    marginLeft: 10,
  },
  pageInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  pageNumber: {
    fontSize: 11,
    color: "#555",
  },
  titleMain: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 20,
    textAlign: "center",
  },
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#ddd",
    marginTop: 5,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    borderBottomStyle: "solid",
    minHeight: 20,
  },
  tableCell: {
    flex: 1,
    padding: 5,
    textAlign: "center",
    borderRightWidth: 1,
    borderRightColor: "#ddd",
    borderRightStyle: "solid",
    fontSize: 11,
  },
  tableCellLabel: {
    flex: 1.2,
    padding: 5,
    textAlign: "left",
    borderRightWidth: 1,
    borderRightColor: "#ddd",
    borderRightStyle: "solid",
    fontSize: 11,
  },
  tableCellValue: {
    flex: 0.8,
    padding: 5,
    textAlign: "right",
    borderRightWidth: 1,
    borderRightColor: "#ddd",
    borderRightStyle: "solid",
    fontSize: 11,
  },
  footer: {
    fontSize: 11,
    textAlign: "center",
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: "#ddd",
  },
  labInfoText: {
    fontSize: 12,
    color: "#333",
  },
  pageFooter: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 20,
    flexDirection: "row",
    fontSize: 11,
  },
  heading: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
  },
  leftColumn: {
    flexDirection: "column",
    flexGrow: 1,
    marginRight: 20,
    fontSize: 12,
  },
  rightColumn: {
    flexDirection: "column",
    alignItems: "flex-start",
    fontSize: 12, 
  },
  optionalData: {
    marginTop: 20,
  },
  optionalDataRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 1,
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    paddingBottom: 5,
  },
  optionalDataLabel: {
    fontSize: 8,  
    width: "90%",
    textAlign: "left",
    marginRight: 10,
  },
  optionalDataValue: {
    fontSize: 8,
    width: "60%",
    textAlign: "right",
  },
});

// ==================== COMPONENTES PDF ====================

interface PDFHeaderProps {
  currentDate: string;
  dataObjects: any;
}

export const PDFHeader: React.FC<PDFHeaderProps> = ({ currentDate, dataObjects }) => {
  const analyst = getFieldValue('Analista', dataObjects);
  
  return (
    <View style={styles.header}>
      <View style={styles.leftColumn}>
        <Image
          style={styles.logo}
          src="/images/logo_ma.png"
        />
      </View>
      <View style={styles.rightColumn}>
        <Text style={styles.pageNumber}>Fecha: {currentDate}</Text>
        <Text style={styles.pageNumber}>Analista que realiza el cálculo: {analyst || 'N/A'}</Text>
      </View>
    </View>
  );
};

interface PDFTitleProps {
  maslReference: string;
  analysisDescription: string;
}

export const PDFTitle: React.FC<PDFTitleProps> = ({
  maslReference,
  analysisDescription,
}) => (
  <Text style={styles.titleMain}>
    {`MUESTRA MASL REF. ${maslReference} - ${analysisDescription} `}
  </Text>
);

interface SampleBasicInfoProps {
  dataObjects: any;
}

export const SampleBasicInfo: React.FC<SampleBasicInfoProps> = ({
  dataObjects,
}) => {
  const analysisCode = getAnalysisCode(dataObjects);
  const layout = getAnalysisLayout(analysisCode);
  const isSrAnalysis = analysisCode === 'Sr89' || analysisCode === 'Sr90';
  const fontSize = isSrAnalysis ? 10 : 12;

  return (
    <View style={styles.header}>
      <View style={styles.leftColumn}>
        {layout.header.leftColumn.map((fieldName, index) => (
          <Text key={`left-${index}`} style={{ fontSize }}>
            {fieldName}: {getFieldValue(fieldName, dataObjects)}
          </Text>
        ))}
      </View>
      <View style={styles.rightColumn}>
        {layout.header.rightColumn.map((fieldName, index) => (
          <Text key={`right-${index}`} style={{ fontSize }}>
            {fieldName}: {getFieldValue(fieldName, dataObjects)}
          </Text>
        ))}
      </View>
    </View>
  );
};

interface PreparationInfoProps {
  dataObjects: any;
}

export const PreparationInfo: React.FC<PreparationInfoProps> = ({
  dataObjects,
}) => {
  const analysisCode = getAnalysisCode(dataObjects);
  const layout = getAnalysisLayout(analysisCode);
  const isSrAnalysis = analysisCode === 'Sr89' || analysisCode === 'Sr90';
  const fontSize = isSrAnalysis ? 10 : 12;

  return (
    <View>
      <Text style={styles.heading}>Resultados Preparación y contaje:</Text>
      <View style={styles.header}>
        <View style={styles.leftColumn}>
          {layout.preparation.leftColumn.map((fieldName, index) => (
            <Text key={`left-prep-${index}`} style={{ fontSize }}>
              {fieldName}: {getFieldValue(fieldName, dataObjects)}
            </Text>
          ))}
        </View>

        <View style={styles.rightColumn}>
          {layout.preparation.rightColumn.map((fieldName, index) => (
            <Text key={`right-prep-${index}`} style={{ fontSize }}>
              {fieldName}: {getFieldValue(fieldName, dataObjects)}
            </Text>
          ))}
        </View>
      </View>
    </View>
  );
};

interface ResultTableProps {
  results: Array<{ label: string; value: string }>;
  title?: string;
}

export const ResultTable: React.FC<ResultTableProps> = ({ results, title }) => (
  <View style={{ flex: 1 }}>
    {title && (
      <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 5 }}>
        {title}
      </Text>
    )}
    <View style={styles.table}>
      {results.map((result, index) => (
        <View style={styles.tableRow} key={`result-${index}`}>
          <Text style={styles.tableCellLabel}>{result.label}</Text>
          <Text style={styles.tableCellValue}>{result.value}</Text>
        </View>
      ))}
    </View>
  </View>
);

interface OutputDataSectionProps {
  outputData: Array<[string, any]>;
  extendedLabels: any;
  title: string;
  typeAnalysisCode: string;
  calculationMethod: string;
  formatOutputValue: (value: any, type: string, decimals: number, fieldKey: string) => string;
}

export const OutputDataSection: React.FC<OutputDataSectionProps> = ({
  outputData,
  extendedLabels,
  title,
  typeAnalysisCode,
  calculationMethod,
  formatOutputValue,
}) => (
  <>
    <Text style={styles.heading}>{title}</Text>
    {outputData
      ?.filter(([key], index) => {
        if (
          typeAnalysisCode === "H3-V" &&
          (calculationMethod === "CURRIE" || calculationMethod === "Currie")
        ) {
          return index < 3;
        }
        return true;
      })
      .map(([key, value], index) => {
        const { label, decimals, type } = extendedLabels[key] || {};
        return (
          <View style={styles.optionalDataRow} key={`data-${index}`}>
            <Text style={styles.optionalDataLabel}>{label}:</Text>
            <Text style={styles.optionalDataValue}>
              {value === 0
                ? "0"
                : value
                ? formatOutputValue(value, type, decimals, key)
                    .toString()
                    .toUpperCase()
                : ""}
            </Text>
          </View>
        );
      })}
  </>
);
